import React, { useRef } from 'react';
import { formatDateDMY, toISODate } from '../utils/dateFormat';

/**
 * DateInputDMY — drop-in replacement for <input type="date">.
 *
 * Displays the selected date as dd/mm/yyyy to the user, while using the native
 * browser calendar picker (click/tap the field) for selection. Emits a
 * synthetic change event with ISO yyyy-mm-dd in `e.target.value` so existing
 * form state wiring doesn't change.
 *
 * Props: all standard <input> props. `value` accepts ISO or any parseable date.
 */
const DateInputDMY = React.forwardRef(function DateInputDMY(
  { value, onChange, className = '', placeholder = 'dd/mm/yyyy', disabled, required, ...rest },
  ref
) {
  const hiddenRef = useRef(null);
  const display = formatDateDMY(value);

  const openPicker = () => {
    if (disabled) return;
    const input = hiddenRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // fallthrough to focus fallback
      }
    }
    input.focus();
    input.click();
  };

  const handleChange = (e) => {
    const iso = e.target.value;
    if (onChange) {
      // Pass through a native-looking event so existing handlers that read
      // e.target.value continue to work unchanged.
      onChange({ ...e, target: { ...e.target, value: iso } });
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (onChange) {
      onChange({ target: { value: '' } });
    }
  };

  return (
    <div
      ref={ref}
      className={`relative inline-flex items-center w-full ${disabled ? 'opacity-60' : ''}`}
      onClick={openPicker}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          openPicker();
        }
      }}
    >
      <input
        type="text"
        value={value ? display : ''}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
        className={`w-full cursor-pointer bg-white ${className || 'p-2 border border-gray-300 rounded'}`}
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear date"
          className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-sm leading-none"
        >
          ×
        </button>
      )}
      <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-sm">
        📅
      </span>
      {/* Hidden native date input to trigger the calendar UI */}
      <input
        ref={hiddenRef}
        type="date"
        value={toISODate(value)}
        onChange={handleChange}
        required={required}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only pointer-events-none absolute inset-0 opacity-0"
        {...rest}
      />
    </div>
  );
});

export default DateInputDMY;
